// components/ProtectedRoute.js
import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notifyError } from '../notify';
import { writeAuditLogSafe } from '../services/auditLogService';

const LogoutAndRedirect = ({ message, reason }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (message) {
      notifyError(message);
    }

    writeAuditLogSafe({
      action: 'Unauthorized route access blocked',
      details: reason || message || 'Access denied by route guard.',
      severity: 'Warning',
      category: 'authorization',
      outcome: 'blocked',
      actor: {
        uid: user?.uid || null,
        email: user?.email || 'unknown',
        role: user?.role || 'unknown',
        name: user?.name || user?.profile?.name || null,
        username: user?.username || user?.profile?.username || null,
        isAuthenticated: !!user,
      },
      path: location.pathname,
      metadata: {
        requiredReason: reason || null,
      },
      tags: ['route', 'authorization', 'blocked'],
    });

    Promise.resolve(logout()).finally(() => {
      navigate('/admin', { replace: true });
    });
  }, [location.pathname, logout, message, navigate, reason, user]);

  return <div className="loading-spinner">Redirecting...</div>;
};

export const ProtectedRoute = ({ children, requiredRole, requiredPermission }) => {
  const { user, loading, hasPermission, isAdmin, isSystemAdmin } = useAuth();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin" replace />;
  }

  // Check role requirements
  if (requiredRole) {
    if (requiredRole === 'admin' && !isAdmin()) {
      return <LogoutAndRedirect message="Unauthorized." reason="Route requires admin role." />;
    }
    if (requiredRole === 'system_admin' && !isSystemAdmin()) {
      return <LogoutAndRedirect message="Unauthorized: sysadmin access only." reason="Route requires system_admin role." />;
    }
  }

  // Check permission requirements
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <LogoutAndRedirect message="Unauthorized: missing required permission." reason={`Missing permission: ${requiredPermission}`} />;
  }

  return children;
};